import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHeader } from "./fordon";

const CANONICAL = "https://www.akhusbilfritid.se/kopvillkor";
const DESC =
  "Köpvillkor för AK Husbil & Fritids butik: frakt och leverans, 14 dagars ångerrätt, returer, reklamation enligt konsumentköplagen och garanti på tillbehör.";

export const Route = createFileRoute("/kopvillkor")({
  head: () => ({
    meta: [
      { title: "Köpvillkor, returer & reklamation | AK Husbil & Fritid" },
      { name: "description", content: DESC },
      { property: "og:title", content: "Köpvillkor, returer & reklamation" },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Hur returnerar jag en vara?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Du har 14 dagars ångerrätt från att du tagit emot varan. Mejla info@akhusbilfritid.se med ordernummer, så får du returinstruktioner. Varan ska vara i väsentligen oförändrat skick.",
              },
            },
            {
              "@type": "Question",
              name: "Hur gör jag en reklamation?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Enligt konsumentköplagen har du rätt att reklamera fel i upp till 3 år. Kontakta oss med ordernummer, beskrivning och bild på felet. Vi står för returfrakten vid godkänd reklamation och lagar, byter eller återbetalar.",
              },
            },
            {
              "@type": "Question",
              name: "Hur lång är leveranstiden?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Lagerlagda tillbehör skickas normalt inom 1–2 arbetsdagar med spårbar frakt inom Sverige. Beställningsvaror och reservdelar tar oftast 3–10 arbetsdagar.",
              },
            },
          ],
        }),
      },
    ],
  }),
  component: KopvillkorPage,
});

function KopvillkorPage() {
  return (
    <>
      <PageHeader
        label="Butik · Juridik"
        title={
          <>
            Köpvillkor, <span className="font-italic-display text-accent">returer</span> &amp;
            reklamation.
          </>
        }
        sub="Enkla villkor, inga konstigheter. Vi följer konsumentköplagen och distansavtalslagen – och löser hellre problem snabbt än formellt."
      />

      <article className="container-edge max-w-3xl py-12 md:py-20">
        <p className="text-sm text-muted-foreground">
          Senast uppdaterad: {new Date().toLocaleDateString("sv-SE")}
        </p>

        <div className="mt-8 space-y-8 md:space-y-10">
          <Block
            h="Vem du handlar av"
            p="Butiken drivs av AK Husbil & Fritid, Kalix. Priser anges i svenska kronor inklusive moms. Vi förbehåller oss rätten att korrigera felaktiga priser och slutsålda artiklar."
          />
          <Block
            h="Betalning"
            p="Betalning sker i vår kassa med kort, Klarna (faktura/delbetalning där det erbjuds) eller Swish. Betalningen hanteras av Shopify och Klarna – vi lagrar aldrig dina kortuppgifter."
          />
          <Block
            h="Frakt och leverans"
            p="Lagerlagda tillbehör skickas normalt inom 1–2 arbetsdagar med spårbar frakt inom Sverige. Beställningsvaror och reservdelar tar oftast 3–10 arbetsdagar; du får alltid besked om leveranstiden avviker. Bor du i närområdet kan du välja upphämtning i Kalix."
          />
          <Block
            h="Ångerrätt – 14 dagar"
            p="Du har 14 dagars ångerrätt från den dag du tar emot varan. Mejla oss med ordernummer så får du returinstruktioner. Varan ska vara i väsentligen oförändrat skick och gärna i originalförpackningen. Vid utnyttjad ångerrätt står du för returfrakten; vi återbetalar inom 14 dagar från att vi tagit emot returen. Ångerrätten gäller inte specialbeställda eller kundanpassade artiklar, öppnade tätningsmedel/kemikalier eller monterade delar."
          />
          <Block
            h="Reklamation – 3 år"
            p="Är varan felaktig eller går sönder på grund av ett ursprungligt fel har du rätt att reklamera i upp till 3 år enligt konsumentköplagen. Kontakta oss med ordernummer, en beskrivning och gärna en bild eller kort film. Vid godkänd reklamation står vi för returfrakten och lagar, byter eller återbetalar varan. Reklamera så snart du upptäcker felet – inom två månader räknas alltid som i rätt tid."
          />
          <Block
            h="Transportskada eller fel leverans"
            p="Kontrollera paketet vid mottagandet. Är emballaget skadat, anmäl det direkt till utlämningsstället och hör av dig till oss inom 7 dagar. Har du fått fel vara skickar vi rätt vara och en förbetald returetikett."
          />
          <Block
            h="Garanti"
            p="Vissa produkter har dessutom tillverkargaranti (t.ex. batterier, värmare och solpaneler). Garantin gäller utöver reklamationsrätten och påverkar inte dina rättigheter enligt lag. Vi hjälper till med garantiärendet mot tillverkaren."
          />
          <Block
            h="Montering hos oss"
            p="Köper du tillbehör som ska monteras kan du boka installation i vår verkstad i Kalix. Arbete som utförs av oss omfattas av vår verkstadsgaranti på utfört arbete."
          />
          <Block
            h="Om vi inte kommer överens"
            p="Vi följer Allmänna reklamationsnämndens (ARN) rekommendationer. Du kan alltid vända dig till ARN eller till EU:s plattform för tvistlösning online om vi inte hittar en lösning tillsammans."
            links={[
              { href: "https://www.arn.se", label: "arn.se – Allmänna reklamationsnämnden" },
              { href: "https://www.hallakonsument.se", label: "hallakonsument.se – konsumentvägledning" },
            ]}
          />
        </div>

        <div className="mt-10 rounded-2xl border bg-muted/30 p-5 md:p-6">
          <h2 className="font-display text-xl md:text-2xl">Retur eller reklamation? Börja här</h2>
          <p className="mt-2 text-sm text-foreground/85">
            Mejla{" "}
            <a
              href="mailto:info@akhusbilfritid.se?subject=Retur%2Freklamation%20-%20ordernummer"
              className="text-accent underline-offset-4 hover:underline"
            >
              info@akhusbilfritid.se
            </a>{" "}
            med ordernummer, vad det gäller och en bild om något är fel. Du får svar inom 24 timmar
            på vardagar.
          </p>
          <div className="mt-4 flex flex-wrap gap-3 text-sm">
            <Link
              to="/butik"
              className="inline-flex min-h-11 items-center rounded-xl border px-4 hover:bg-muted"
            >
              Till butiken
            </Link>
            <Link
              to="/kontakt"
              className="inline-flex min-h-11 items-center rounded-xl border px-4 hover:bg-muted"
            >
              Kontakta oss
            </Link>
          </div>
        </div>
      </article>
    </>
  );
}

function Block({
  h,
  p,
  links,
}: {
  h: string;
  p: string;
  links?: { href: string; label: string }[];
}) {
  return (
    <section>
      <h2 className="font-display text-2xl md:text-3xl">{h}</h2>
      <p className="mt-3 text-foreground/85">{p}</p>
      {links && (
        <ul className="mt-3 space-y-1.5 text-sm">
          {links.map((l) => (
            <li key={l.href}>
              <a
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent underline-offset-4 hover:underline"
              >
                {l.label} →
              </a>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
